import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import { TileInfo, openTileUrl } from 'app/home/index.functions';
import React, { lazy, useEffect, useState } from 'react';
import IconMi from 'components/common/icon/IconMi';
import TopPanel from './TopPanel';
import { usePrepareViewerData } from 'components/element-viewer/hooks/usePrepareViewerData';
import { FilterType } from 'components/external-reference/Filter';
import { useQuery } from '@tanstack/react-query';
import { AnyElementsViewerDataType, ElementViewerDataResponse, ElementViewerDataType } from 'components/element-viewer';
import { viewerAPI } from 'api/viewer';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardElementType } from 'tools/types';
import { MetricViewerDataType } from 'components/metric/MetricViewer';
import { ElementPreviewSummary } from 'app/home/tile/preview/ElementPreviewSummary';
import useBundleTranslation from 'i18n';
import useBookmarks, { IBookmarksContext } from 'components/external-reference/hooks/useBookmarks';
import { ExternalReportViewerDataType } from 'app/extreport/ExternalReportViewer';
import { useHomeContext } from 'app/home/context';
import { createPortal } from 'react-dom';
import { formatElementDashboardName, openInNewTab } from 'tools/tools';
import { escape } from 'lodash';
import { SiblingTabType } from 'app/extreport/viewer/SiblingTabsMenu';
import { IPartsVisibility } from 'components/element-viewer/ControlsPanel';
import { dispatchCustomEvent } from 'tools/events';
const MetricPreview = lazy(() => import('./MetricPreview'));
export default function Preview({
    tile,
    onNextPreviewClick,
    hideNextPrevButtons,
    dataParams,
    refetchData,
}: {
    tile: TileInfo | null;
    onNextPreviewClick: (next: boolean) => void;
    hideNextPrevButtons?: boolean;
    dataParams?: { [key: string]: string };
    refetchData?: () => void;
}) {
    const { t } = useBundleTranslation(['app/home']);

    const [showPreviewPopup, setShowPreviewPopup] = useState(true);
    const [userChartOverlay, setUserChartOverlay] = useState<number>(0);
    const [filters, setFilters] = useState<Array<FilterType>>([]);
    const ctx = useHomeContext();
    const setTilePreview = ctx.setTilePreview;
    const customEvents = ctx.customEvents;
    const partsVisibility = ctx.visibility;
    const elementId = Number(tile?.element_id);
    const segmentValueId = Number(tile?.segment_value_id);

    const [searchParams] = useSearchParams();
    const queryKey = `element_preview_${tile?.user_dashboard_element_instance_id}`;
    const {
        data: viewerRequestData,
        refetch,
        isRefetching,
    } = useQuery<ElementViewerDataResponse, Error>({
        queryKey: [queryKey],
        queryFn: () => {
            return viewerAPI.loadElementData(
                elementId,
                Number(tile?.segment_value_id),
                true,
                userChartOverlay,
                false,
                true,
                undefined,
                searchParams,
                -1,
                -1,
                true,
            );
        },
    });

    const { viewerData, viewerExtraData } = usePrepareViewerData(
        userChartOverlay,
        setUserChartOverlay,
        setFilters,
        'preview',
        viewerRequestData,
    );

    const onPreviewHide = () => {
        setShowPreviewPopup(false);
        setTilePreview(null);
    };

    const navigate = useNavigate();

    const openElement = (
        type: DashboardElementType,
        elementId: number,
        segmentValueId?: number,
        e?: React.MouseEvent,
    ) => {
        if (null != tile) {
            if (undefined !== dataParams && undefined !== dataParams['on_click']) {
                if (
                    'url' == dataParams['on_click'] &&
                    undefined !== dataParams['redirect_url'] &&
                    dataParams['redirect_url'] > ''
                ) {
                    const newTab =
                        (undefined !== dataParams['new_tab'] && 'Y' == dataParams['new_tab']) ||
                        (undefined !== e && e.ctrlKey);

                    if (newTab) openInNewTab(dataParams['redirect_url']);
                    else document.location = dataParams['redirect_url'];
                    return;
                } else if ('event' == dataParams['on_click']) {
                    document.dispatchEvent(
                        new CustomEvent('mi-click', {
                            bubbles: false,
                            detail: {
                                props: dataParams,
                            },
                        }),
                    );
                    onPreviewHide();
                    return;
                }
            }
            return openTileUrl(tile, navigate, e);
        }
    };

    // TODO:
    const isEmptyInstance: any = 'N';
    const baseScr =
        process.env.REACT_APP_BACKEND_API_BASE_URL +
        `/data/content/index/preview/element/${elementId}/segment/${segmentValueId}?dashboard=Y`; // /dashboard/Y/external/Y

    const imageUrl =
        viewerData?.elementData?.displayFullImage && !('Y' == isEmptyInstance)
            ? baseScr
            : isEmptyInstance == 'Y'
              ? '/img/default_preview_no_data.png?dashboard=Y'
              : baseScr;
    const [imageTimestamp, setImageTimestamp] = useState(0);

    const onFavoritesChange = () => {
        refetchData && refetchData();
        refetch();
    };

    return tile && showPreviewPopup && viewerData?.elementData && viewerExtraData ? (
        <Dialog
            fullWidth
            maxWidth={'popupXl'}
            scroll={'body'}
            sx={{
                '.MuiDialog-paper': { overflow: 'visible', marginTop: '100px' },
                '.MuiDialog-container:after': {
                    content: 'none',
                },
            }}
            open={showPreviewPopup}
            onClose={onPreviewHide}
        >
            {isRefetching && <LoadingPlaceholder />}
            <DialogTitle id="alert-dialog-title" sx={{ py: '10px' }}>
                <Stack direction={'row'} justifyContent={'space-between'}>
                    <Stack direction={'row'} spacing={1}>
                        {true !== hideNextPrevButtons ? (
                            <>
                                <Button
                                    variant={'light'}
                                    startIcon={<IconMi icon="chevron-left" />}
                                    onClick={() => {
                                        onNextPreviewClick(false);
                                    }}
                                >
                                    {t('previous')}
                                </Button>
                                <Button
                                    variant={'light'}
                                    endIcon={<IconMi icon="chevron-right" />}
                                    onClick={() => {
                                        onNextPreviewClick(true);
                                    }}
                                >
                                    {t('next_preview')}
                                </Button>
                            </>
                        ) : (
                            <Stack>&nbsp;</Stack>
                        )}
                    </Stack>
                    <IconButton
                        aria-label="close"
                        onClick={onPreviewHide}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: 'primary.main',
                        }}
                    >
                        <IconMi icon="times" fontSize="16" />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{ overflow: 'visible', py: '10px' }}>
                {viewerData &&
                    (viewerData.elementData.row.type == 'external report' ? (
                        <TopPanelWithBookmark
                            customOnTitleClick={customEvents?.preview?.title_click}
                            partsVisibility={partsVisibility?.preview}
                            filters={filters}
                            tile={tile}
                            viewerExtraData={viewerExtraData as ExternalReportViewerDataType}
                            viewerData={viewerData}
                            openElement={openElement}
                            onBookmarkChange={(timestamp: number) => {
                                setImageTimestamp(timestamp);
                            }}
                            onFavoritesChange={onFavoritesChange}
                        />
                    ) : (
                        <TopPanel
                            partsVisibility={partsVisibility?.preview}
                            customOnTitleClick={customEvents?.preview?.title_click}
                            viewerData={viewerData}
                            tileName={
                                undefined !== tile.element_dashboard_name_with_formatting &&
                                tile.element_dashboard_name_with_formatting > ''
                                    ? formatElementDashboardName(tile.element_dashboard_name_with_formatting)
                                    : escape(tile.element_dashboard_name ?? '')
                            }
                            openElement={openElement}
                            onFavoritesChange={onFavoritesChange}
                        />
                    ))}
            </DialogContent>
            <DialogContent sx={{ overflow: 'visible' }}>
                <PreviewContent
                    customOnImageClick={customEvents?.preview?.image_click}
                    imageUrl={imageUrl + '&t=' + imageTimestamp}
                    refetch={refetch}
                    openElement={openElement}
                    viewerData={viewerData}
                    viewerExtraData={viewerExtraData}
                    segmentValueId={segmentValueId}
                />
            </DialogContent>
        </Dialog>
    ) : (
        <LoadingPlaceholder />
    );
}

function TopPanelWithBookmark({
    viewerExtraData,
    viewerData,
    filters,
    tile,
    openElement,
    onBookmarkChange,
    onFavoritesChange,
    customOnTitleClick,
    partsVisibility,
}: {
    viewerExtraData: ExternalReportViewerDataType;
    viewerData: ElementViewerDataType;
    filters: Array<FilterType>;
    tile: TileInfo;
    openElement: (type: DashboardElementType, elementId: number, segmentValueId: number) => void;
    onBookmarkChange: (timestamp: number) => void;
    onFavoritesChange: () => void;
    customOnTitleClick?: boolean;
    partsVisibility?: IPartsVisibility;
}) {
    const {
        bookmarks,
        APIUpdateBookmark,
        APISaveAsNewBookmark,
        APIDeleteBookmark,
        APIDuplicateBookmark,
        APISortBookmarks,
        APIIsLoading,
    } = useBookmarks(viewerExtraData, viewerData, filters, []);

    const [lastBookmark, setLastBookmark] = useState(bookmarks?.[0] ?? null);
    const [loaded, setLoaded] = useState(false);
    useEffect(() => {
        if (JSON.stringify(lastBookmark) == JSON.stringify(bookmarks?.[0] ?? {})) {
            return;
        }
        setLastBookmark(bookmarks?.[0] ?? null);
    }, [JSON.stringify(bookmarks)]);

    useEffect(() => {
        if (lastBookmark == null) {
            return;
        }
        if (!loaded) {
            setLoaded(true);
            return;
        }
        onBookmarkChange(Date.now());
    }, [lastBookmark]);

    let siblingTabs: Array<SiblingTabType> = [];
    if (viewerData.elementData.row.type == 'external report') {
        siblingTabs = viewerExtraData.tabsData.siblingTabs;
    }

    const bookmarksContext: IBookmarksContext = {
        showIcon: filters.length + siblingTabs.length > 0,
        bookmarks: bookmarks,
        filters: filters,
        siblingTabs: siblingTabs,
        selectedReferenceId: 0,
        APIUpdateBookmark: APIUpdateBookmark,
        APISaveAsNewBookmark: APISaveAsNewBookmark,
        APIDeleteBookmark: APIDeleteBookmark,
        APIDuplicateBookmark: APIDuplicateBookmark,
        APISortBookmarks: APISortBookmarks,
        APIIsLoading: APIIsLoading,
    };

    return (
        <>
            {APIIsLoading &&
                createPortal(<LoadingPlaceholder relativeToParent sx={{ zIndex: 90000 }} />, document.body)}
            <TopPanel
                partsVisibility={partsVisibility}
                customOnTitleClick={customOnTitleClick}
                bookmarksContext={bookmarksContext}
                viewerData={viewerData}
                tileName={tile.element_dashboard_name ?? ''}
                openElement={openElement}
                onFavoritesChange={onFavoritesChange}
            />
        </>
    );
}

export function PreviewContent({
    viewerData,
    viewerExtraData,
    segmentValueId,
    openElement,
    refetch,
    imageUrl,
    customOnImageClick,
}: {
    viewerData: ElementViewerDataType;
    viewerExtraData: AnyElementsViewerDataType;
    segmentValueId: number;
    openElement: (type: DashboardElementType, elementId: number, segmentValueId?: number) => void;
    refetch: () => void;
    imageUrl: string;
    customOnImageClick?: boolean;
}) {
    const { t } = useBundleTranslation(['components/common/element']);
    const [imageLoaded, setImageLoaded] = useState(false);

    const viewerProps = {
        crumbsData: viewerData.crumbsData,
        related: [],
        filters: [],
        onFiltersChange: () => {},
        onSegmentValueChange: () => {},
        segmentValueId: segmentValueId,
        onFavoritesChange: () => {},
        onRelatedElementSelection: () => {},
        onFilterChange: () => {},
        refetch: refetch,
    };

    const elementId = viewerData.elementData.row.elementId;
    const handleClick = () => {
        if (customOnImageClick) {
            dispatchCustomEvent('mi-preview', {
                action: 'image-click',
                payload: { element: viewerData.elementData.row },
            });
            return;
        }
        openElement(viewerData.elementData.row.type, elementId, segmentValueId);
    };

    const onLoad = () => {
        setImageLoaded(true);
    };

    const summaryComponent = (
        <ElementPreviewSummary
            elementInfo={viewerData?.elementData}
            viewerRequestExtraData={viewerExtraData}
            onToggleVisibility={() => {}}
            isVisible
            isPreview
        />
    );

    if (['metric', 'multi-metric chart'].includes(viewerData.elementData.row.type)) {
        return (
            <Stack direction="row" sx={{ overflow: 'hidden' }}>
                <MetricPreview
                    openElement={openElement}
                    elementInfo={viewerData?.elementData}
                    viewerRequestExtraData={viewerExtraData as MetricViewerDataType}
                    viewerRequestData={viewerData}
                    {...viewerProps}
                />
                {summaryComponent}
            </Stack>
        );
    }

    if (
        viewerData.elementData.row.type == 'external report' &&
        undefined !== viewerData.elementData?.noToken &&
        true === viewerData.elementData?.noToken
    ) {
        openElement(viewerData.elementData.row.type, elementId, segmentValueId);
        return null;
    }

    if (
        viewerData.elementData.row.type == 'external report' ||
        viewerData.elementData.row.type == 'other external content'
    ) {
        return (
            <Stack direction="row" sx={{ overflow: 'hidden' }}>
                <Box flexGrow={1} sx={{ overflow: 'hidden' }}>
                    <Box sx={{ display: 'inline-flex', maxWidth: '100%', cursor: 'pointer' }} onClick={handleClick}>
                        <img style={{ maxWidth: '100%' }} id="preview_image_el" src={imageUrl} onLoad={onLoad} />
                    </Box>
                    {!imageLoaded && viewerData.elementData.row.type == 'external report' && (
                        <Stack justifyContent={'center'} alignItems={'center'} spacing={2} sx={{ minHeight: '200px' }}>
                            <CircularProgress size={24} />
                            <Typography>{t('preview_preparing')}</Typography>
                            <Button variant={'text'} sx={{ width: '30%' }} onClick={handleClick}>
                                {t('skip_preview')}
                            </Button>
                        </Stack>
                    )}
                </Box>

                {summaryComponent}
            </Stack>
        );
    }

    if (viewerData.elementData.row.type == 'internal report') {
        return (
            <Stack direction="row" sx={{ overflow: 'hidden' }}>
                <Stack direction="column">
                    <Box flexGrow={1}>
                        <span>
                            <iframe
                                style={{ border: 'none', overflow: 'hidden', width: 750, height: 516 }}
                                src={`/report/${elementId}/segment/${segmentValueId}/preview?width=750&height=500&view_type=tile`}
                            />
                        </span>
                    </Box>
                    <Box display="flex" justifyContent="center" alignItems="center">
                        <Button variant="light" startIcon={<IconMi icon="view" />} onClick={handleClick}>
                            {t('view_full_report')}
                        </Button>
                    </Box>
                </Stack>
                {summaryComponent}
            </Stack>
        );
    }

    return null;
}
